import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { authService } from '@/features/auth/services'
import { queryClient } from '@/lib/query-client'
import type { Profile, UserSettings } from '@/shared/types'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: Profile | null
  settings: UserSettings | null
  loading: boolean
  signUp: (email: string, password: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  refreshSettings: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshProfile = async () => {
    if (!user) return
    const { data } = await authService.getProfile(user.id)
    setProfile(data)
  }

  const refreshSettings = async () => {
    if (!user) return
    const { data } = await authService.getSettings(user.id)
    setSettings(data)
  }

  // Load profile & settings without blocking auth
  const loadUserData = async (userId: string) => {
    try {
      const [profileResult, settingsResult] = await Promise.allSettled([
        authService.getProfile(userId),
        authService.getSettings(userId)
      ])
      if (profileResult.status === 'fulfilled') setProfile(profileResult.value.data)
      if (settingsResult.status === 'fulfilled') setSettings(settingsResult.value.data)
    } catch {
      // Non-critical — don't break auth flow
    }
  }

  useEffect(() => {
    // Initialize auth — ensure Supabase client is fully synced before setting loading=false
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          await loadUserData(session.user.id)
        }
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }

    initAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user) {
        loadUserData(session.user.id)
      } else {
        setProfile(null)
        setSettings(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string) => {
    const { error } = await authService.signUp(email, password)
    if (error) throw error
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await authService.signIn(email, password)
    if (error) {
      // Supabase returns generic 'Invalid login credentials' for both wrong email and wrong password.
      // Differentiate by checking if the email exists in auth.users via RPC.
      if (error.message === 'Invalid login credentials') {
        try {
          const { data: exists } = await supabase.rpc('check_email_exists', { p_email: email })
          if (exists === false) {
            throw new Error('EMAIL_NOT_FOUND')
          }
          throw new Error('WRONG_PASSWORD')
        } catch (rpcError) {
          // If RPC itself fails, re-throw with the specific code if we already set it
          if (rpcError instanceof Error && (rpcError.message === 'EMAIL_NOT_FOUND' || rpcError.message === 'WRONG_PASSWORD')) {
            throw rpcError
          }
          // RPC failed — fall back to generic
          throw error
        }
      }
      throw error
    }
  }

  const signOut = async () => {
    await authService.signOut()
    await queryClient.cancelQueries({ queryKey: ['meta-posts'] })
    queryClient.removeQueries({ queryKey: ['meta-posts'] })
    setUser(null)
    setSession(null)
    setProfile(null)
    setSettings(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        settings,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        refreshSettings,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
